import { useEffect, useRef, useState } from 'react'
import { useThemeMode } from '../../../_metronic/partials'
import { getCSS, getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'
import { ApexOptions } from 'apexcharts'
import { MultiValue, SingleValue } from 'react-select'
import { SelectItem } from '../charts/core/_models'
import { meterType, predictionAlgorithmType } from '../reports/core/_models'
import { getAnalyticChartData, getCompareChartData } from '../reports/core/_requests'
import { useIntl } from 'react-intl'
import moment from 'moment'
import DatePicker from '../../utils/Form/Widgets/DatePicker'

type Props = {
  companyFilter: SingleValue<SelectItem>
  siteFilter: MultiValue<SelectItem>
  metersFilter: MultiValue<SelectItem>
  meterTypeFilter: SingleValue<meterType>
  readingType: string
  predictionAlgorithmFilter: MultiValue<predictionAlgorithmType>
  customDateFilter: boolean
}
const AnalyticChart: React.FC<Props> = ({
  companyFilter,
  siteFilter,
  metersFilter,
  meterTypeFilter,
  readingType,
  predictionAlgorithmFilter,
  customDateFilter,
}) => {
  const [chartType, setChartType] = useState('line')
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()
  const intl = useIntl()
  let [data, setData] = useState(null)
  const [rangeStart, setRangeStart] = useState<moment.Moment>(moment())

  const params = {
    isLoggedIn: true,
    companyId: companyFilter ? companyFilter.value : '',
    siteId: siteFilter,
    metersId: metersFilter,
    meterType: meterTypeFilter,
    readingType: readingType,
    algorithmType: predictionAlgorithmFilter,
    startDate: rangeStart.format('YYYY-MM-DD'),
    endDate: rangeStart.format('YYYY-MM-DD'),
  }
  const fetchAnalyticChartData = async () => {
    try {
      getAnalyticChartData({ ...params }).then((response) => {
        setTimeout(async () => {
          const data = await getAnalyticChartData(params)
          setData(data)
        }, 1000)
      })
    } catch (error) {
      console.log('Error fetching analytic chart data:', error)
    }
  }

  useEffect(() => {
    if (companyFilter) {
      fetchAnalyticChartData()
    }
  }, [companyFilter, siteFilter, metersFilter, meterTypeFilter, rangeStart])

  useEffect(() => {
    if (companyFilter) {
      fetchAnalyticChartData()
    }
  }, [predictionAlgorithmFilter])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(chartRef.current, getChartOptions(height, chartType, data))
    if (chart) {
      chart.render()
    }

    return chart
  }
  const handleChartTypeChange = (e: any) => {
    const newChartType = e.target.value
    setChartType(newChartType)
  }

  useEffect(() => {
    const chart = refreshChart()
    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, chartType, data])

  let title = ''
  if (readingType == 'last_one_year') {
    title = intl.formatMessage({ id: 'last_one_year_prediction_consumption' })
  } else if (readingType == 'last_one_month') {
    title = intl.formatMessage({ id: 'last_one_month_prediction_consumption' })
  } else if (readingType == 'yesterday') {
    title = intl.formatMessage({ id: 'yesterday_prediction_consumption' })
  } else if (readingType == 'last_one_week') {
    title = intl.formatMessage({ id: 'last_one_week_prediction_consumption' })
  } else if (readingType == 'next_week') {
    title = intl.formatMessage({ id: 'next_week_prediction_consumption' })
  } else if (readingType == 'custom') {
    title = intl.formatMessage({ id: 'yesterday_prediction_consumption_average' })
  }
  let className = 'card-xl-stretch mb-5 mb-xl-8'
  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{title}</span>
        </h3>

        {customDateFilter && (
          <DatePicker
            title='Select Date'
            onChange={(selectedDate) => {
              if (selectedDate) {
                setRangeStart(moment(selectedDate))
              }
            }}
          />
        )}
        <div className='card'>
          <select
            className='form-select form-select-sm w-125px'
            style={{ margin: '10px' }}
            value={chartType}
            onChange={handleChartTypeChange}
          >
            <option key='area' value='area'>
              {intl.formatMessage({ id: 'area' })}
            </option>
            <option key='bar' value='bar'>
              {intl.formatMessage({ id: 'bar' })}
            </option>
            <option key='line' value='line'>
              {intl.formatMessage({ id: 'line' })}
            </option>
            <option key='radar' value='radar'>
              {intl.formatMessage({ id: 'radar' })}
            </option>
          </select>
        </div>
      </div>

      <div className='card-body'>
        <div ref={chartRef} id='kt_charts_widget_2_chart' style={{ height: '350px' }}></div>
      </div>
    </div>
  )
}

function getChartOptions(height: number, type: string, data: any): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  return {
    series: data?.datasets,
    chart: {
      fontFamily: 'inherit',
      //@ts-ignore
      type: type,
      height: height,
      toolbar: {
        show: false,
      },
    },

    legend: {
      show: true,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
    },
    xaxis: {
      categories: data?.labels,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return val + ' kWh'
        },
      },
    },
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}

export default AnalyticChart
